/**
 * 🔒 Página de cambio de contraseña obligatorio
 * Se muestra cuando el usuario ingresa con una contraseña temporal
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  LockReset as LockResetIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const CambioPasswordObligatorio = () => {
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);

  useEffect(() => {
    // Verificar que el usuario esté autenticado y deba cambiar contraseña
    const verificarUsuario = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/auth/me`, { withCredentials: true });
        if (!res.data.debe_cambiar_password) {
          // Si no debe cambiar contraseña, redirigir al dashboard
          window.location.href = '/dashboard';
        }
        setUsuario(res.data);
      } catch (err) {
        // No autenticado, redirigir al login
        window.location.href = '/login';
      }
    };

    verificarUsuario();
  }, []);

  const validarPassword = (password) => {
    if (password.length < 8) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!/[A-Z]/.test(password)) {
      return 'La contraseña debe contener al menos una mayúscula';
    }
    if (!/[a-z]/.test(password)) {
      return 'La contraseña debe contener al menos una minúscula';
    }
    if (!/[0-9]/.test(password)) {
      return 'La contraseña debe contener al menos un número';
    }
    return null;
  };

  const handleCambiarPassword = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones
    const errorValidacion = validarPassword(nuevaPassword);
    if (errorValidacion) {
      setError(errorValidacion);
      return;
    }

    if (nuevaPassword !== confirmarPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/cambiar-password-obligatoria`,
        { nueva_password: nuevaPassword },
        { withCredentials: true }
      );

      alert('✅ Contraseña actualizada exitosamente');
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!usuario) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <LockResetIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Cambio de Contraseña Obligatorio
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Has iniciado sesión con una contraseña temporal.
              Por seguridad, debes establecer una nueva contraseña permanente.
            </Typography>
          </Alert>

          <form onSubmit={handleCambiarPassword}>
            <TextField
              fullWidth
              label="Nueva Contraseña"
              type={mostrarPassword ? 'text' : 'password'}
              value={nuevaPassword}
              onChange={(e) => setNuevaPassword(e.target.value)}
              margin="normal"
              required
              autoFocus
              helperText="Mínimo 8 caracteres, debe incluir mayúsculas, minúsculas y números"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarPassword(!mostrarPassword)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {mostrarPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Confirmar Contraseña"
              type={mostrarConfirmar ? 'text' : 'password'}
              value={confirmarPassword}
              onChange={(e) => setConfirmarPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {mostrarConfirmar ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !nuevaPassword || !confirmarPassword}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Cambiar Contraseña'}
            </Button>
          </form>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Requisitos de contraseña segura:</strong>
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>
                <Typography variant="caption">Mínimo 8 caracteres</Typography>
              </li>
              <li>
                <Typography variant="caption">Al menos una letra mayúscula</Typography>
              </li>
              <li>
                <Typography variant="caption">Al menos una letra minúscula</Typography>
              </li>
              <li>
                <Typography variant="caption">Al menos un número</Typography>
              </li>
            </ul>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default CambioPasswordObligatorio;
